import React, {useCallback, useMemo} from 'react';

import {InstrumentedWaveLoader} from '../../../utility/InstrumentedLoader';
import {ColorPalette} from '../colorPalette/ColorPalette';
import {RunColorOptions, SemanticLegendSettings, YAxisValues} from '../types';
import {SemanticLegendBuckets} from './SemanticLegendBuckets';
import {SemanticLegendCategories} from './SemanticLegendCategories';
import {SemanticLegendConfigValue} from './SemanticLegendConfigValue';
import {DEFAULT_SEMANTIC_LEGEND_SETTINGS} from './SemanticLegendDefaults';
import {SemanticLegendExcludedRunColor} from './SemanticLegendExcludedRunColor';
import {SemanticLegendKey} from './SemanticLegendKey';
import {SemanticLegendNumBuckets} from './SemanticLegendNumBuckets';
import {SemanticLegendRunColorOptions} from './SemanticLegendRunColorOptions';
import {useSemanticLegendSettingsContext} from './SemanticLegendSettingsContextProvider';
import {SemanticLegendYValue} from './SemanticLegendYValue';

const objNoop = {};

export const SemanticLegendColorTab = () => {
  const {
    hasActiveGrouping,
    configKeys,
    semanticLegendSettings,
    semanticLegendSettingsLoading,
    semanticLegendRunColorOptions,
    isMetricBasedColors,
    semanticLegendPalette,
    updateSemanticLegendRunColorOptions,
    updateSemanticLegendSettingsOptimistically,
  } = useSemanticLegendSettingsContext();

  const showMetricBasedColors = !hasActiveGrouping && isMetricBasedColors;

  const isConfigBasedColors =
    semanticLegendSettings?.yAxisValue === YAxisValues.Config_Categories ||
    semanticLegendSettings?.yAxisValue === YAxisValues.Config_Number;

  // Computed values from the current semantic legend settings
  const computedValues = useMemo(
    () => ({
      currentKey: semanticLegendSettings?.key ?? '',
      yAxisValue:
        semanticLegendSettings?.yAxisValue ??
        DEFAULT_SEMANTIC_LEGEND_SETTINGS.yAxisValue,
      numberOfBuckets:
        semanticLegendSettings?.yAxisValue === YAxisValues.Config_Categories
          ? (semanticLegendSettings?.categories?.length ??
            DEFAULT_SEMANTIC_LEGEND_SETTINGS.numberOfBuckets)
          : (semanticLegendSettings?.numberOfBuckets ??
            DEFAULT_SEMANTIC_LEGEND_SETTINGS.numberOfBuckets),
      categories: semanticLegendSettings?.categories ?? [],
      configValuesCategories:
        semanticLegendSettings?.configValuesCategories ?? [],
      bucketCustomizations:
        semanticLegendSettings?.bucketCustomizations ?? objNoop,
      buckets: semanticLegendSettings?.buckets ?? [],
      configValuesForRun: semanticLegendSettings?.configValuesForRun ?? [],
      excludedRunColor: semanticLegendSettings?.excludedRunColor ?? '',
    }),
    [semanticLegendSettings]
  );

  const updateSetting = useCallback(
    (value: Partial<SemanticLegendSettings>) => {
      updateSemanticLegendSettingsOptimistically?.({
        ...semanticLegendSettings,
        ...value,
      });
    },
    [semanticLegendSettings, updateSemanticLegendSettingsOptimistically]
  );

  const updateRunColorOptions = useCallback(
    (value: RunColorOptions) => {
      updateSemanticLegendRunColorOptions?.(value);
    },
    [updateSemanticLegendRunColorOptions]
  );

  if (semanticLegendSettingsLoading) {
    return (
      <div className="absolute flex h-1/2 w-full items-center justify-center">
        <InstrumentedWaveLoader
          size="huge"
          name="semantic-legend-settings-loading"
        />
      </div>
    );
  }

  return (
    <div
      data-test="workspace-semantic-legend-color-settings"
      className={'flex h-full w-full flex-col gap-20 py-16'}>
      <SemanticLegendRunColorOptions
        hasActiveGrouping={hasActiveGrouping}
        runColorOptions={
          semanticLegendRunColorOptions ?? RunColorOptions.OriginalProjectColors
        }
        onRunColorOptionsChange={updateRunColorOptions}
      />
      {showMetricBasedColors && (
        <div
          className={
            'ml-10 flex h-full w-full flex-col gap-10 border-l-2 border-moon-350'
          }>
          <SemanticLegendKey
            currentKey={computedValues.currentKey}
            type={computedValues.yAxisValue}
            configKeys={configKeys}
            updateSetting={updateSetting}
          />
        </div>
      )}
      {showMetricBasedColors &&
        semanticLegendSettings?.key !==
          DEFAULT_SEMANTIC_LEGEND_SETTINGS.key && (
          <>
            {semanticLegendRunColorOptions ===
              RunColorOptions.OriginalProjectColors && <ColorPalette />}
            <div
              className={
                'ml-10 flex h-full w-full flex-col gap-10 border-l-2 border-moon-350'
              }>
              {!isConfigBasedColors && (
                <SemanticLegendYValue
                  yValue={computedValues.yAxisValue}
                  updateSetting={updateSetting}
                />
              )}
              {isConfigBasedColors && (
                <SemanticLegendConfigValue
                  yValue={computedValues.yAxisValue}
                  updateSetting={updateSetting}
                />
              )}
            </div>
            <div className={'flex h-full w-full flex-col'}>
              <SemanticLegendNumBuckets
                numberOfBuckets={computedValues.numberOfBuckets}
                updateSetting={updateSetting}
                yValue={computedValues.yAxisValue}
              />
              {semanticLegendSettings?.yAxisValue !==
                YAxisValues.Config_Categories && (
                <SemanticLegendBuckets
                  bucketCustomizations={computedValues.bucketCustomizations}
                  buckets={computedValues.buckets}
                  updateSetting={updateSetting}
                />
              )}
            </div>
            <div className={'flex h-full w-full flex-col'}>
              {semanticLegendSettings?.yAxisValue ===
                YAxisValues.Config_Categories && (
                <SemanticLegendCategories
                  categories={computedValues.categories}
                  configValuesCategories={computedValues.configValuesCategories}
                  categoryCustomizations={computedValues.bucketCustomizations}
                  configValuesForRun={computedValues.configValuesForRun}
                  semanticLegendPalette={semanticLegendPalette}
                  updateSetting={updateSetting}
                />
              )}
            </div>
            <div className={'flex h-full w-full flex-col'}>
              <SemanticLegendExcludedRunColor
                excludedRunColor={computedValues.excludedRunColor}
                updateSetting={updateSetting}
              />
            </div>
          </>
        )}
    </div>
  );
};
